<script setup>
import { ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  attrKey: { type: String, required: true },
  value: { type: [Boolean, Number, String], default: null },
  definition: { type: Object, required: true },
  label: { type: String, required: true },
  description: { type: String, default: '' },
});

const emit = defineEmits(['update', 'remove']);
const { t } = useI18n();

const inputValue = ref(props.value);
const isEditing = ref(false);
const fieldRoot = ref(null);

const saveValue = () => {
  emit('update', props.attrKey, Number(inputValue.value));
  isEditing.value = false;
};

const cancelEditing = () => {
  inputValue.value = props.value;
  isEditing.value = false;
};

const handleClickOutside = e => {
  if (fieldRoot.value && !fieldRoot.value.contains(e.target)) {
    saveValue();
  }
};

watch(isEditing, editing => {
  if (editing) {
    document.addEventListener('mousedown', handleClickOutside);
  } else {
    document.removeEventListener('mousedown', handleClickOutside);
  }
});

watch(
  () => props.value,
  newValue => {
    if (!isEditing.value) inputValue.value = newValue;
  }
);

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});

const startEditing = () => {
  inputValue.value = props.value;
  isEditing.value = true;
};
</script>

<template>
  <div
    ref="fieldRoot"
    class="flex group rounded-lg items-center gap-2 px-3 py-1.5 transition-colors hover:bg-n-slate-2 dark:hover:bg-n-solid-1 is-editable"
  >
    <!-- Header: icon + label + actions -->
    <div class="flex items-center gap-2 flex-shrink-0">
      <Icon
        :icon="definition.icon || 'i-lucide-tag'"
        class="size-5 opacity-50 text-woot-600 dark:text-woot-200 flex-shrink-0"
      />
      <p
        class="font-medium text-sm text-slate-900 dark:text-white truncate !m-0 truncate flex-1 !m-0"
      >
        {{ label }}
      </p>
    </div>

    <!-- Value -->

    <!-- Boolean -->

    <div v-if="definition.type === 'boolean'" class="w-full px-3">
      <Switch
        :model-value="!!value"
        class="my-3"
        @update:model-value="newVal => emit('update', attrKey, newVal)"
      />
    </div>

    <!-- Number: click-to-edit -->
    <template v-else-if="definition.type === 'number'">
      <input
        v-if="isEditing"
        v-model="inputValue"
        type="number"
        step="1"
        autofocus
        class="w-full text-sm border border-n-brand rounded focus:outline-none focus:ring-1 focus:ring-n-brand/50 bg-n-background text-n-slate-12 !mb-0 dark:!bg-neutral-950"
        @keydown.enter.prevent="saveValue"
        @keyup.escape="cancelEditing"
        @blur="saveValue"
      />
      <button
        v-else
        type="button"
        class="w-full text-left px-2 py-1 text-sm rounded hover:bg-n-slate-2 dark:hover:bg-n-solid-2 transition-colors h-10"
        :class="
          value != null && value !== '' ? 'text-n-slate-12' : 'text-n-slate-9'
        "
        @click="startEditing"
      >
        {{ value != null && value !== '' ? value : '—' }}
      </button>
    </template>

    <!-- List: select -->
    <select
      v-else-if="definition.type === 'list'"
      :value="value"
      class="w-full text-sm border border-n-weak rounded focus:outline-none focus:ring-1 focus:ring-n-brand/50 bg-white dark:bg-neutral-950 text-n-slate-12 !mb-0"
      @change="emit('update', attrKey, $event.target.value)"
    >
      <option v-for="opt in definition.options || []" :key="opt" :value="opt">
        {{
          t(
            `SETTINGS.SPACES.ATTRIBUTES.${attrKey.toUpperCase()}.OPTIONS.${opt.toUpperCase()}`
          )
        }}
      </option>
    </select>
    <div
      class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 justify-end"
    >
      <button
        v-tooltip="description"
        type="button"
        class="flex p-1 text-n-slate-9 hover:text-n-slate-12 rounded w-8 h-8 items-center justify-center"
      >
        <Icon icon="i-lucide-info" class="size-4" />
      </button>
      <button
        type="button"
        class="flex p-1 text-n-slate-9 hover:text-red-500 rounded w-8 h-8 items-center justify-center"
        @click="emit('remove', attrKey)"
      >
        <Icon icon="i-lucide-trash-2" class="size-4" />
      </button>
    </div>
  </div>
</template>
